import Modal from '../common/Modal'
import TextareaField from '../common/TextareaField'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { handlePatchRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { showToaster } from '../../utils/show-toaster.util'
import USER_TYPE from '../../constants/userType'
import { CONTENT_REPORT_STATUS } from '../../constants/supportTicket'
import UserTypeDisplay from '../common/UserTypeDisplay'
import { CONTENT_TYPES } from '../../constants/contentTypes'

export default function ResolveReportModal({ isOpen, onClose, report, content }) {
  const [deactivateChoice, setDeactivateChoice] = useState(true)
  const [deactivateReason, setDeactivateReason] = useState('')
  const isUserType = [USER_TYPE.retailer, USER_TYPE.wholeseller, USER_TYPE.customer].includes(
    report.contentType
  )

  const updateStatusMutation = useMutation({
    mutationFn: (payload) => handlePatchRequest(`/admin/reports/${report.id}/resolve`, payload),
    onSuccess: () => {
      QueryKeys.adminReportsTickets.invalidateAll()
      QueryKeys.adminReportTicketById.invalidate(report.id.toString())
      onClose()
    },
    onError: () => {
      showToaster('error', 'Failed to update report status')
    },
  })

  const handleSubmit = async () => {
    const payload = {
      status: CONTENT_REPORT_STATUS.resolved.key,
      deActivate: deactivateChoice,
      ...(!deactivateChoice && { remainActiveReason: deactivateReason }),
    }
    updateStatusMutation.mutateAsync(payload)
  }

  const getMessage = () => {
    if (report.contentType === CONTENT_TYPES.product) {
      const affectedShops = content?.shopsCount
      return (
        <p className="text-sm text-gray-500 mb-4">
          You are about to resolve the report for this product.{' '}
          {affectedShops > 0 && (
            <span className="font-bold">
              This product is sold by {affectedShops} {affectedShops === 1 ? 'shop' : 'shops'}.
            </span>
          )}
        </p>
      )
    }
    return (
      <p className="text-sm text-gray-500 mb-4">
        You are about to resolve the report for this{' '}
        <UserTypeDisplay userType={report.contentType} />. Please confirm the action and provide a
        reason if you choose to keep it active.
      </p>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 max-w-md w-full">
        <h2 className="text-lg font-bold mb-4">Resolve Report</h2>
        {getMessage()}

        <div className=" gap-8 mt-6 mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              name={isUserType ? 'suspend' : 'deactivate'}
              checked={deactivateChoice}
              onChange={(e) => setDeactivateChoice(e.target.checked)}
              className="accent-orange-600 w-4 h-4"
            />
            <span className="text-md flex gap-1 items-center">
              Deactivate this
              <UserTypeDisplay userType={report.contentType} />
            </span>
          </label>
        </div>
        {!deactivateChoice && (
          <div className="mb-4">
            <TextareaField
              label={<>Reason for keeping active</>}
              id={'deactivate-reason'}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              required
              placeholder="Enter reason here..."
              rows={3}
            />
          </div>
        )}
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-neutral-200"
            disabled={updateStatusMutation.isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded bg-secondary-600 text-white"
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? 'Updating...' : 'Resolve'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
